import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  AvatarGroup,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";
import { getStatusChip, formatCurrency } from "../utils";
import { getCadence } from "lib/cadence";
import { ImageAvatar } from "../../shared/ImageAvatar";
import { ActiveCircleActions } from "./ActiveCircleActions";
import { PendingCircleActions } from "./PendingCircleActions";
import type { Group } from "../types";

interface GroupCardProps {
  group: Group;
  onViewDetails: (groupId: string) => void;
  onJoinCircle: (circleId: string) => void;
  onDeclineCircle: (circleId: string) => void;
}

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  onViewDetails,
  onJoinCircle,
  onDeclineCircle,
}) => {
  const theme = useTheme();
  const { color, label } = getStatusChip(group.status);

  const statusColors: Record<string, string> = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
    default: theme.palette.grey[500],
  };
  const statusColor = statusColors[color] ?? statusColors.default;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 4,
          bgcolor: "background.paper",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          overflow: "hidden",
          transition: "box-shadow 0.25s, transform 0.25s",
          "&:hover": {
            boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
            transform: "translateY(-3px)",
          },
        }}
      >
        {/* Status accent bar */}
        <Box sx={{ height: 4, bgcolor: statusColor }} />

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "text.primary",
                lineHeight: 1.3,
                maxWidth: "72%",
              }}
              noWrap
            >
              {group.title}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.3,
                borderRadius: 1,
                bgcolor: alpha(statusColor, 0.1),
                border: `1px solid ${alpha(statusColor, 0.25)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: statusColor,
                  letterSpacing: "0.03em",
                }}
              >
                {label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AvatarGroup
              max={3}
              sx={{
                "& .MuiAvatar-root": {
                  width: 28,
                  height: 28,
                  fontSize: "0.7rem",
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                },
              }}
            >
              {group.members.map((member, index) => (
                <ImageAvatar
                  key={index}
                  displayImg={member.displayImg}
                  firstName={member.firstName}
                />
              ))}
            </AvatarGroup>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              {group.members.length} members
            </Typography>
          </Box>
        </Box>

        {/* Hero contribution */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 0.5,
            }}
          >
            {getCadence(group.intervalUnit, group.intervalCount).adjective}{" "}
            Contribution
          </Typography>
          <Typography
            sx={{
              fontSize: "1.75rem",
              fontWeight: 700,
              color: getAmountColor(group),
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {group.status === "completed"
              ? "Completed"
              : formatCurrency(group.monthlyContribution)}
            {group.status !== "completed" && (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 400,
                  color: "text.secondary",
                  ml: 0.5,
                }}
              >
                {getCadence(group.intervalUnit, group.intervalCount).suffix}
              </Typography>
            )}
          </Typography>
        </Box>

        <Divider sx={{ mx: 3 }} />

        {/* Date stat */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 0.4,
            }}
          >
            {group.nextPayout ? "Next Payout" : "Due Date"}
          </Typography>
          <Typography
            sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.primary" }}
          >
            {group.nextPayout
              ? dayjs(group.nextPayout).format("MMM D, YYYY")
              : group.dueDate}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ px: 3, pb: 3, mt: "auto", display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onViewDetails(group.id)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "0.82rem",
              borderColor: "divider",
              color: "text.secondary",
              "&:hover": {
                borderColor: "text.primary",
                color: "text.primary",
                bgcolor: "transparent",
              },
            }}
          >
            View Details
          </Button>

          {group.status === "active" && (
            <ActiveCircleActions
              circleId={group.id}
              paymentStatus={group.paymentStatus}
              canWithdraw={group.canWithdraw}
              onViewDetails={onViewDetails}
            />
          )}

          {group.status === "pending" && group.acceptanceStatus && (
            <PendingCircleActions
              circleId={group.id}
              acceptanceStatus={group.acceptanceStatus}
              canAddSelf={group.canAddSelf}
              onJoinCircle={onJoinCircle}
              onDecline={onDeclineCircle}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
};

function getAmountColor(group: Group): string {
  if (
    group.status === "pending" ||
    group.status === "completed" ||
    (group.status === "active" && group.paymentStatus.isPayee)
  )
    return "primary.main";
  if (group.paymentStatus.status === "Paid") return "success.main";
  // An unpaid/processing amount stays neutral — the Pay Now button and status
  // chip carry that state. Tinting the money figure itself read as an alert.
  return "text.primary";
}
